import { type ColumnType } from 'antd/lib/table/interface';

import { isTimestampLike, timestampCompare } from './FirestoreUtils';
import { booleanCompare } from './Sort';
import { includesNoCase } from './String';

//#region Primitives
// Functions that operate on already-extracted values.

const ERROR = {}; // sentinel for a mapper that threw

// To force sorting into broad groups, then sorting within groups, we can
// return a string with an initial letter declaring the group. For example,
// 'failure' values like null, undefined, and ERROR, all go into a 'Z'
// group, then sort according to their second letter. Otherwise 'valid'
// values all go into an 'A' group and sort in the regular lexicographic
// way among themselves.
function stringKey(x: unknown): string {
    if (x === null) return 'ZA';
    if (x === undefined) return 'ZB';
    if (x === ERROR) return 'ZC';
    return 'A' + String(x);
}

/**
 * Coerces both parameters to strings and returns their
 * `String.localeCompare` result. Exception: null and undefined are sorted
 * to the end (null first), and mapper errors after that.
 */
export function localeCompareAsStrings(a: unknown, b: unknown): number {
    return stringKey(a).localeCompare(stringKey(b));
}

export function comparePossibleTimestamps(a: unknown, b: unknown): number {
    if (isTimestampLike(a)) {
        return isTimestampLike(b) ? timestampCompare(a, b) : 1;
    }
    if (isTimestampLike(b)) return -1;
    return 0;
}

// Same (group, value)-in-one-string trick as stringKey: the known roles
// form group 'A' in ascending power (default < manager < admin); anything
// else drops to the later group 'Z', then further grouped by stringKey -
// meaning present but invalid strings will sort after valid strings,
// *then* null, undefined, and ERROR in that order.
function userRoleKey(x: unknown): string {
    if (x === 'default') return 'AA';
    if (x === 'manager') return 'AB';
    if (x === 'admin') return 'AC';
    return 'Z' + stringKey(x);
}

export function compareUserRoles(a: unknown, b: unknown): number {
    return userRoleKey(a).localeCompare(userRoleKey(b));
}

//#region Factories
// Given a field accessor, produce a ready antd column sorter/filter.
// The single accessor is applied to both operands.

function safeMap<RecordType>(
    mapper: (record: RecordType) => unknown,
    record: RecordType,
    callerLabel: string
): unknown {
    // A comparator/predicate that throws mid-sort/filter takes the whole
    // table render down, so degrade a bad row rather than crash.
    try {
        return mapper(record);
    } catch (e) {
        console.error('%s: mapper threw for record %O', callerLabel, record, e);
        return ERROR;
    }
}

/**
 * A `sorter` that compares a field as strings (coercing non-strings).
 */
export function makeStringSorter<RecordType>(
    mapper: (record: RecordType) => unknown
): ColumnType<RecordType>['sorter'] {
    return (a, b) =>
        localeCompareAsStrings(
            safeMap(mapper, a, 'makeStringSorter'),
            safeMap(mapper, b, 'makeStringSorter')
        );
}

/**
 * A `sorter` that compares a field as Firestore timestamps. Non-timestamps
 * sort to the end (see comparePossibleTimestamps).
 */
export function makeTimestampSorter<RecordType>(
    mapper: (record: RecordType) => unknown
): ColumnType<RecordType>['sorter'] {
    return (a, b) =>
        comparePossibleTimestamps(
            safeMap(mapper, a, 'makeTimestampSorter'),
            safeMap(mapper, b, 'makeTimestampSorter')
        );
}

export function makeUserRoleSorter<RecordType>(
    mapper: (record: RecordType) => unknown
): ColumnType<RecordType>['sorter'] {
    return (a, b) =>
        compareUserRoles(
            safeMap(mapper, a, 'makeUserRoleSorter'),
            safeMap(mapper, b, 'makeUserRoleSorter')
        );
}

/**
 * A `sorter` that orders a boolean field false-first (falsy values, including
 * nullish, before truthy).
 */
export function makeBooleanSorter<RecordType>(
    mapper: (record: RecordType) => unknown
): ColumnType<RecordType>['sorter'] {
    return (a, b) =>
        booleanCompare(
            safeMap(mapper, a, 'makeBooleanSorter'),
            safeMap(mapper, b, 'makeBooleanSorter')
        );
}

/**
 * An `onFilter` that case-insensitively substring-matches the search value
 * against a field. Unlike the sorter, this gates rather than coerces: a
 * non-string field (or search value) simply does not match.
 */
export function makeStringFilter<RecordType>(
    mapper: (record: RecordType) => unknown
): ColumnType<RecordType>['onFilter'] {
    return (value, record) => {
        const haystack = safeMap(mapper, record, 'makeStringFilter');
        return includesNoCase(haystack, value);
    };
}
